import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChefHat, MapPin, Clock, Star, DollarSign, Phone, Mail, Globe, Utensils, Users, Calendar, AlertCircle, Loader, Heart, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import BackButton from '../components/BackButton';
import { getVenuePictures } from '../lib/storage';

const RestaurantDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restaurant, setRestaurant] = useState<any>(null);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const [showLightbox, setShowLightbox] = useState(false);
  const [pictures, setPictures] = useState<{
    gallery: string[];
    menu: string[];
    interior: string[];
    exterior: string[];
  }>({
    gallery: [],
    menu: [],
    interior: [],
    exterior: []
  });

  useEffect(() => {
    fetchRestaurantDetails();
  }, [id]);

  const fetchRestaurantDetails = async () => {
    try {
      setLoading(true);
      console.log('Fetching restaurant details for ID:', id);
      
      const { data, error: fetchError } = await supabase
        .from('restaurants')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) {
        console.error('Error fetching restaurant:', fetchError);
        throw fetchError;
      }
      
      console.log('Restaurant data:', data);
      setRestaurant(data);

      // Fetch and normalize pictures
      try {
        const normalizedPictures = await getVenuePictures(data.id, 'restaurant');
        console.log('Normalized pictures:', normalizedPictures);
        setPictures(normalizedPictures);
        
        // Set first exterior image as selected if available
        if (normalizedPictures.exterior.length > 0) {
          setSelectedImageUrl(normalizedPictures.exterior[0]);
        } else if (normalizedPictures.gallery.length > 0) {
          setSelectedImageUrl(normalizedPictures.gallery[0]);
        } else if (normalizedPictures.interior.length > 0) {
          setSelectedImageUrl(normalizedPictures.interior[0]);
        }
      } catch (pictureError) {
        console.error('Error fetching pictures:', pictureError);
        // Don't throw here, we can still show the restaurant without pictures
      }
    } catch (err) {
      console.error('Error fetching restaurant details:', err);
      setError(err instanceof Error ? err.message : 'Failed to load restaurant details');
    } finally {
      setLoading(false);
    }
  };

  const getAllImages = () => {
    return [
      ...pictures.exterior,
      ...pictures.interior,
      ...pictures.gallery
    ];
  };

  const handleImageClick = (url: string) => {
    setSelectedImageUrl(url);
    setShowLightbox(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error || !restaurant) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          <span>{error || 'Restaurant not found'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center space-x-4 mb-8">
        <BackButton />
        <h1 className="text-3xl font-bold text-gray-900">{restaurant.name}</h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Image Gallery */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Main Image */}
            <div className="relative aspect-video cursor-pointer" onClick={() => selectedImageUrl && handleImageClick(selectedImageUrl)}>
              {selectedImageUrl ? (
                <img
                  src={selectedImageUrl}
                  alt={restaurant.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    console.error(`Failed to load image: ${selectedImageUrl}`);
                    (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=2000&auto=format&fit=crop';
                  }}
                />
              ) : (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                  <ChefHat className="h-16 w-16 text-gray-400" />
                </div>
              )}
            </div>
            
            {/* Thumbnail Gallery */}
            <div className="p-4 grid grid-cols-4 gap-4">
              {getAllImages().length > 0 ? (
                getAllImages().map((url, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImageUrl(url)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImageUrl === url ? 'border-orange-500 scale-95' : 'border-transparent hover:border-orange-200'
                    }`}
                  >
                    <img
                      src={url}
                      alt={`${restaurant.name} view ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        console.error(`Failed to load thumbnail: ${url}`);
                        (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=150&auto=format&fit=crop';
                      }}
                    />
                  </button>
                ))
              ) : (
                <div className="col-span-4 py-4 text-center text-gray-500">
                  <p>Keine Bilder verfügbar</p>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Über {restaurant.name}</h2>
            <p className="text-gray-600 leading-relaxed">{restaurant.description}</p>
          </div>

          {/* Menu Photos */}
          {pictures.menu.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Speisekarte</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {pictures.menu.map((url, index) => (
                  <div 
                    key={index} 
                    className="aspect-[3/4] rounded-lg overflow-hidden cursor-pointer"
                    onClick={() => handleImageClick(url)}
                  >
                    <img
                      src={url}
                      alt={`${restaurant.name} menu page ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        console.error(`Failed to load menu image: ${url}`);
                        (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=300&auto=format&fit=crop';
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Info */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 text-gray-600">
                  <Star className="h-5 w-5 text-yellow-500" />
                  <span className="font-medium">{restaurant.rating || '4.5'}</span>
                  <span className="text-sm">({restaurant.reviews_count || '0'} reviews)</span>
                </div>
                <span className="text-gray-600">{restaurant.price_range}</span>
              </div>

              <div className="flex items-center space-x-2 text-gray-600">
                <ChefHat className="h-5 w-5" />
                <span>{restaurant.cuisine_type}</span>
              </div>

              <div className="flex items-center space-x-2 text-gray-600">
                <MapPin className="h-5 w-5" />
                <span>{restaurant.address}</span>
              </div>

              <div className="flex items-center space-x-2 text-gray-600">
                <Clock className="h-5 w-5" />
                <span>{restaurant.opening_hours}</span>
              </div>

              <div className="flex items-center space-x-2 text-gray-600">
                <Phone className="h-5 w-5" />
                <span>{restaurant.phone}</span>
              </div>

              <div className="flex items-center space-x-2 text-gray-600">
                <Mail className="h-5 w-5" />
                <span>{restaurant.email}</span>
              </div>
            </div>
          </div>

          {/* Dietary Options */}
          {restaurant.dietary_options && restaurant.dietary_options.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                <Utensils className="h-5 w-5 text-orange-500" />
                <span>Ernährungsoptionen</span>
              </h3>
              <div className="flex flex-wrap gap-2">
                {restaurant.dietary_options.map((option: string) => (
                  <span
                    key={option}
                    className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm"
                  >
                    {option}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-4">
            <button
              onClick={() => navigate(`/reservations?restaurant=${restaurant.id}`)}
              className="w-full bg-orange-500 text-white px-4 py-3 rounded-xl hover:bg-orange-600 transition flex items-center justify-center space-x-2"
            >
              <Calendar className="h-5 w-5" />
              <span>Reserviere einen Tisch</span>
            </button>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {showLightbox && selectedImageUrl && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setShowLightbox(false)}
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition"
            onClick={() => setShowLightbox(false)}
          >
            <X className="h-8 w-8" />
          </button>
          <img
            src={selectedImageUrl}
            alt="Full size view"
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
            onError={(e) => {
              console.error(`Failed to load lightbox image: ${selectedImageUrl}`);
              (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=2000&auto=format&fit=crop';
            }}
          />
        </div>
      )}
    </div>
  );
};

export default RestaurantDetails;